import type { Workspace } from '@/lib/api/workspaces.js';

/** The active rows of the switcher in the groups it shows them in, top to bottom. */
export interface SwitcherGroups {
  /** The Personal workspace. It is always first and never pinnable. */
  personal: Workspace[];
  /** The caller's pinned workspaces, in the order they set. */
  pinned: Workspace[];
  /** Every other active workspace, in the server's name order. */
  others: Workspace[];
}

/** Whether the caller pinned this workspace. Personal never counts, whatever the server sends. */
function isPinned(workspace: Workspace): boolean {
  return (
    workspace.kind !== 'personal' &&
    typeof workspace.pinnedPosition === 'number'
  );
}

/** Sort order of the pinned workspaces: by the position the caller set. */
function byPinPosition(first: Workspace, second: Workspace): number {
  return (first.pinnedPosition ?? 0) - (second.pinnedPosition ?? 0);
}

/** Split the active workspaces into the switcher's groups: Personal, then pinned, then the rest. */
export function orderSwitcherWorkspaces(active: Workspace[]): SwitcherGroups {
  return {
    personal: active.filter((workspace) => workspace.kind === 'personal'),
    pinned: active.filter(isPinned).sort(byPinPosition),
    others: active.filter(
      (workspace) => workspace.kind !== 'personal' && !isPinned(workspace),
    ),
  };
}

/**
 * The caller's stored pin list in order, archived workspaces included. Every save sends this whole
 * list, so an archived workspace keeps its pin and comes back in place when it is restored.
 */
export function pinnedIds(workspaces: Workspace[]): string[] {
  return workspaces
    .filter(isPinned)
    .sort(byPinPosition)
    .map((workspace) => workspace.id);
}

/** The pin list with `id` added at the end. */
export function withPinned(ids: string[], id: string): string[] {
  return [...ids, id];
}

/** The pin list without `id`. */
export function withoutPinned(ids: string[], id: string): string[] {
  return ids.filter((pinnedId) => pinnedId !== id);
}

/**
 * The pin list with `id` and `neighbourId` in each other's place. A move swaps a row with the row the
 * user sees next to it, so a hidden archived pin or a search filter never makes a move look like a no-op.
 */
export function swapped(
  ids: string[],
  id: string,
  neighbourId: string,
): string[] {
  return ids.map((pinnedId) =>
    pinnedId === id ? neighbourId : pinnedId === neighbourId ? id : pinnedId,
  );
}
